// Drawer — side panel anchored to the inline-end edge (flips in RTL): dimmed
// overlay, full-height sliding panel with an icon-tile header, scrollable body
// and an optional footer action row. Rendered in a portal; closes on Escape /
// overlay click; restores focus on close. The header accepts either the
// icon/title/subtitle props or a fully custom `header` node.

import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import IconButton from './IconButton';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Secondary line under the title. */
  subtitle?: ReactNode;
  /** Kebab-case Icon name shown in the brand-tinted header tile. */
  icon?: string;
  /** Replaces the icon/title/subtitle block (close button always renders). */
  header?: ReactNode;
  /** Action row below the body — rendered only when provided. */
  footer?: ReactNode;
  /** Close when the overlay (outside the panel) is clicked. Default false. */
  closeOnOverlay?: boolean;
  className?: string;
  children: ReactNode;
}

const Drawer = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  header,
  footer,
  closeOnOverlay = false,
  className = '',
  children,
}: DrawerProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape closes; focus moves into the panel on open and back on close.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const onOverlayMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlay && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className='app-drawer' onMouseDown={onOverlayMouseDown}>
      <div
        ref={panelRef}
        className={['app-drawer__panel', className].filter(Boolean).join(' ')}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className='app-drawer__header'>
          {header ?? (
            <>
              {icon && (
                <span className='app-drawer__tile'>
                  <Icon name={icon} size={20} />
                </span>
              )}
              <span className='app-drawer__heading'>
                <h2 id={titleId} className='app-drawer__title'>
                  {title}
                </h2>
                {subtitle && <span className='app-drawer__subtitle'>{subtitle}</span>}
              </span>
            </>
          )}
          <IconButton
            icon='x'
            variant='ghost'
            className='app-drawer__close'
            aria-label={t('drawer_close')}
            onClick={onClose}
          />
        </header>

        <div className='app-drawer__body'>{children}</div>

        {footer && <footer className='app-drawer__footer'>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
};

export default Drawer;
